import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, X, ArrowUp, ArrowDown, Star, Image as ImageIcon,
  Grid, Rows, GripVertical,
} from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// ─── Iter 71B — canonical template defaults ─────────────────────────────
//
// These are the ONLY sources of truth for the four fallback-aware
// collections. They are re-used by both the SRC rendering (via
// `setServiceData` / `AnnouncementBar`) and the Visual Editor (below),
// so a template default is never forked into a second hardcoded copy.
//
// The editor consumes them through `resolveWithFallback(...)` which
// deep-clones before handing them to local `useState` — protecting the
// module-level constants from being mutated by editing.
import {
  TRUST_POINTS as SVC_TRUST_POINTS_DEFAULTS,
  WHY_US as SVC_WHY_US_DEFAULTS,
  PROCESS_STEPS as SVC_PROCESS_STEPS_DEFAULTS,
} from "@/template-service/lib/_data";
import { DEFAULT_MESSAGES as ECOM_ANNOUNCEMENT_DEFAULTS } from "@/pages/template-ecommerce/components/AnnouncementBar";

/**
 * Resolve editor initial state from saved DB values OR canonical
 * template defaults, whichever applies. Deep-clones the source so
 * editing local state can never mutate the shared module-level
 * defaults (which would leak into every other business's canvas +
 * editor).
 *
 * @param {Array|undefined} saved   – array read from `publicPayload.website_content.<field>`
 * @param {Array}           defaults – canonical template default constant (imported above)
 * @param {(item) => any}   adapt    – normalize each source item into the editor's local shape
 * @returns {Array} A safe-to-mutate array in the editor's local shape
 */
function resolveWithFallback(saved, defaults, adapt) {
  const source = Array.isArray(saved) && saved.length > 0 ? saved : (defaults || []);
  // Deep clone: `structuredClone` handles plain objects, arrays,
  // primitives — all we need here. JSON round-trip fallback covers
  // older runtimes.
  const cloned = typeof structuredClone === "function"
    ? structuredClone(source)
    : JSON.parse(JSON.stringify(source));
  return cloned.map(adapt);
}

/**
 * Shared Phase-3 section-collection editors. Each edits a top-level
 * website_content array field (testimonials / faqs / pricing_plans /
 * team_members / gallery), PATCHing the full list back via
 * `saveAll(patch)`. The templates read from these fields on next
 * refetch so the canvas reflects changes right away.
 */

/* -------------------------------------------------------------------------- */
/* Testimonials                                                               */
/* -------------------------------------------------------------------------- */

export function TestimonialsEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    return normalize(wc.testimonials || [], (t) => ({
      quote: t.quote || t.text || "",
      name: t.name || t.author || "",
      role: t.role || t.title || t.service || "",
      location: t.location || "",
      avatar_url: t.avatar_url || t.image_url || "",
      rating: Number(t.rating) || 5,
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((t) => (t.quote || "").trim()).map((t) => ({
        quote: t.quote,
        name: t.name,
        role: t.role,
        location: t.location,
        avatar_url: t.avatar_url,
        rating: Math.max(1, Math.min(5, Number(t.rating) || 5)),
      }));
      await saveAll({ testimonials: clean });
      toast.success("Testimonials saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save testimonials");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="testimonials-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ quote: "New testimonial", name: "Customer", role: "", location: "", avatar_url: "", rating: 5 })}
      addLabel="Add testimonial"
      saving={saving}
      onSave={save}
      renderItem={(t, i, patch) => (
        <div className="space-y-2">
          <Textarea
            rows={3}
            value={t.quote}
            onChange={(e) => patch({ quote: e.target.value })}
            placeholder="Quote…"
            data-testid={`testimonial-quote-${i}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={t.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="Author name"
              data-testid={`testimonial-name-${i}`}
            />
            <Input
              value={t.role}
              onChange={(e) => patch({ role: e.target.value })}
              placeholder="Role / Title"
              data-testid={`testimonial-role-${i}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-600">Rating</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => patch({ rating: n })}
                data-testid={`testimonial-rating-${i}-${n}`}
                className="p-0.5"
              >
                <Star className={`w-4 h-4 ${n <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
              </button>
            ))}
          </div>
        </div>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* FAQ                                                                        */
/* -------------------------------------------------------------------------- */

export function FaqEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    return normalize(wc.faqs || [], (f) => ({
      q: f.q || f.question || "",
      a: f.a || f.answer || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((f) => (f.q || "").trim() && (f.a || "").trim());
      await saveAll({ faqs: clean });
      toast.success("FAQs saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save FAQs");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="faq-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ q: "New question", a: "Answer here…" })}
      addLabel="Add question"
      saving={saving}
      onSave={save}
      renderItem={(f, i, patch) => (
        <div className="space-y-2">
          <Input
            value={f.q}
            onChange={(e) => patch({ q: e.target.value })}
            placeholder="Question"
            data-testid={`faq-question-${i}`}
          />
          <Textarea
            rows={3}
            value={f.a}
            onChange={(e) => patch({ a: e.target.value })}
            placeholder="Answer"
            data-testid={`faq-answer-${i}`}
          />
        </div>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Pricing                                                                    */
/* -------------------------------------------------------------------------- */

export function PricingEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    return normalize(wc.pricing_plans || [], (p) => ({
      name: p.name || "",
      price: p.price || "",
      price_suffix: p.price_suffix || "",
      description: p.description || "",
      features: Array.isArray(p.features) ? p.features.map((x) => String(x)) : [],
      cta_text: p.cta_text || "",
      cta_link: p.cta_link || "",
      featured: !!p.featured,
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.map((p) => ({ ...p, features: (p.features || []).filter((f) => (f || "").trim()) }));
      await saveAll({ pricing_plans: clean });
      toast.success("Pricing saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save pricing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="pricing-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ name: "Starter", price: "$29", price_suffix: "/mo", description: "", features: ["Feature 1"], cta_text: "Get started", cta_link: "#", featured: false })}
      addLabel="Add plan"
      saving={saving}
      onSave={save}
      renderItem={(p, i, patch) => (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input value={p.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Plan name" data-testid={`pricing-name-${i}`} />
            <div className="flex gap-1">
              <Input value={p.price} onChange={(e) => patch({ price: e.target.value })} placeholder="$29" className="flex-1" data-testid={`pricing-price-${i}`} />
              <Input value={p.price_suffix} onChange={(e) => patch({ price_suffix: e.target.value })} placeholder="/mo" className="w-16" data-testid={`pricing-suffix-${i}`} />
            </div>
          </div>
          <Textarea rows={2} value={p.description} onChange={(e) => patch({ description: e.target.value })} placeholder="Short plan tagline" data-testid={`pricing-description-${i}`} />
          <FeaturesEditor
            features={p.features || []}
            onChange={(features) => patch({ features })}
            testIdPrefix={`pricing-features-${i}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input value={p.cta_text} onChange={(e) => patch({ cta_text: e.target.value })} placeholder="Button text" data-testid={`pricing-cta-text-${i}`} />
            <Input value={p.cta_link} onChange={(e) => patch({ cta_link: e.target.value })} placeholder="/link" data-testid={`pricing-cta-link-${i}`} />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Highlighted plan (featured)</span>
            <Switch checked={!!p.featured} onCheckedChange={(v) => patch({ featured: v })} data-testid={`pricing-featured-${i}`} />
          </div>
        </div>
      )}
    />
  );
}

function FeaturesEditor({ features, onChange, testIdPrefix }) {
  const add = () => onChange([...(features || []), "New feature"]);
  const remove = (i) => onChange(features.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(features.map((f, idx) => idx === i ? v : f));
  return (
    <div className="space-y-1">
      {features.map((f, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input
            value={f}
            onChange={(e) => update(i, e.target.value)}
            placeholder="Feature"
            className="text-xs flex-1"
            data-testid={`${testIdPrefix}-${i}`}
          />
          <button onClick={() => remove(i)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`${testIdPrefix}-remove-${i}`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="w-full py-1 border border-dashed border-gray-200 rounded-md text-[11px] font-bold text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-1"
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus className="w-3 h-3" /> Add feature
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Team                                                                       */
/* -------------------------------------------------------------------------- */

export function TeamEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    return normalize(wc.team_members || [], (m) => ({
      name: m.name || "",
      role: m.role || m.title || "",
      bio: m.bio || "",
      image_url: m.image_url || m.photo || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((m) => (m.name || "").trim());
      await saveAll({ team_members: clean });
      toast.success("Team saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save team");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="team-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ name: "New teammate", role: "Role", bio: "", image_url: "" })}
      addLabel="Add team member"
      saving={saving}
      onSave={save}
      renderItem={(m, i, patch) => (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input value={m.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Name" data-testid={`team-name-${i}`} />
            <Input value={m.role} onChange={(e) => patch({ role: e.target.value })} placeholder="Role" data-testid={`team-role-${i}`} />
          </div>
          <Textarea rows={2} value={m.bio} onChange={(e) => patch({ bio: e.target.value })} placeholder="Short bio" data-testid={`team-bio-${i}`} />
          <ImageUpload
            url={m.image_url}
            onChange={(u) => patch({ image_url: u })}
            testIdPrefix={`team-image-${i}`}
          />
        </div>
      )}
    />
  );
}

function ImageUpload({ url, onChange, testIdPrefix }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data?.image_url) onChange(data.image_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload image");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      {url ? (
        <img src={url} alt="" className="w-10 h-10 rounded object-cover border border-gray-200" data-testid={`${testIdPrefix}-preview`} />
      ) : (
        <div className="w-10 h-10 rounded border-2 border-dashed border-gray-300 flex items-center justify-center">
          <ImageIcon className="w-4 h-4 text-gray-300" />
        </div>
      )}
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="px-2 py-1 border border-gray-200 rounded-md text-[11px] font-bold hover:border-black flex items-center gap-1 disabled:opacity-50"
        data-testid={`${testIdPrefix}-upload`}
      >
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Upload"}
      </button>
      <Input
        value={url}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste URL"
        className="text-[11px] flex-1"
        data-testid={`${testIdPrefix}-url`}
      />
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Gallery                                                                    */
/* -------------------------------------------------------------------------- */

export function GalleryEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    return normalize(wc.gallery || [], (g) => ({
      url: g.url || g.image_url || "",
      caption: g.caption || "",
      alt: g.alt || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [mode, setMode]   = useState("grid");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((g) => (g.url || "").trim());
      await saveAll({ gallery: clean });
      toast.success("Gallery saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save gallery");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1 mb-3 text-xs">
        <span className="text-gray-600 mr-2">Display:</span>
        <button
          onClick={() => setMode("grid")}
          className={`px-2 py-1 rounded ${mode === "grid" ? "bg-emerald-500 text-white" : "border border-gray-200"}`}
          data-testid="gallery-editor-mode-grid"
        >
          <Grid className="w-3 h-3 inline mr-1" /> Grid
        </button>
        <button
          onClick={() => setMode("slider")}
          className={`px-2 py-1 rounded ${mode === "slider" ? "bg-emerald-500 text-white" : "border border-gray-200"}`}
          data-testid="gallery-editor-mode-slider"
        >
          <Rows className="w-3 h-3 inline mr-1" /> Slider
        </button>
      </div>
      <ListEditorShell
        testId="gallery-editor"
        items={items}
        setItems={setItems}
        onAdd={() => ({ url: "", caption: "", alt: "" })}
        addLabel="Add image"
        saving={saving}
        onSave={save}
        renderItem={(g, i, patch) => (
          <div className="space-y-2">
            <ImageUpload url={g.url} onChange={(u) => patch({ url: u })} testIdPrefix={`gallery-image-${i}`} />
            <Input value={g.caption} onChange={(e) => patch({ caption: e.target.value })} placeholder="Caption (optional)" data-testid={`gallery-caption-${i}`} />
            <Input value={g.alt} onChange={(e) => patch({ alt: e.target.value })} placeholder="Alt text (screen readers)" data-testid={`gallery-alt-${i}`} />
          </div>
        )}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Iter 68 — collection editors for previously-unwired testids. Each one     */
/* PATCHes straight into `website_content.<field>`.                          */
/*                                                                            */
/* Field mapping (verified against backend allowlist in business.py):         */
/*  • TrustPointsEditor         → website_content.trust_points   [{label,icon}]*/
/*  • WhyChooseUsEditor         → website_content.why_choose_us  [{title,description,icon}]*/
/*  • ProcessStepsEditor        → website_content.how_it_works   [{title,description}]*/
/*  • AnnouncementMessagesEditor → website_content.announcement_messages [{icon,text}]*/
/* -------------------------------------------------------------------------- */

const TRUST_ICON_OPTIONS = [
  { value: "MapPin",         label: "Location pin" },
  { value: "ShieldCheck",    label: "Shield check" },
  { value: "MessageSquare",  label: "Message square" },
  { value: "Zap",            label: "Zap" },
  { value: "BadgeCheck",     label: "Badge check" },
];

const WHY_ICON_OPTIONS = [
  { value: "MessageCircle",  label: "Message circle" },
  { value: "ReceiptText",    label: "Receipt" },
  { value: "Clock",          label: "Clock" },
  { value: "Home",           label: "Home" },
  { value: "CalendarCheck",  label: "Calendar check" },
  { value: "Award",          label: "Award" },
];

const ANNOUNCEMENT_ICON_OPTIONS = [
  { value: "truck",     label: "Truck" },
  { value: "shield",    label: "Shield" },
  { value: "refresh",   label: "Refresh" },
  { value: "sparkles",  label: "Sparkles" },
];

function IconSelect({ value, options, onChange, testId }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-gray-200 rounded-md px-2 py-1.5 w-full"
      data-testid={testId}
    >
      <option value="">(pick icon)</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function TrustPointsEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    // Iter 71B — fall back to the SRC template's canonical
    // TRUST_POINTS defaults when DB has no saved values so the editor
    // opens with the same items the canvas is showing.
    return resolveWithFallback(wc.trust_points, SVC_TRUST_POINTS_DEFAULTS, (tp) => ({
      label: tp.label || tp.title || "",
      icon: tp.icon || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((tp) => (tp.label || "").trim());
      await saveAll({ trust_points: clean });
      toast.success("Trust points saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save trust points");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="trust-points-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ label: "New trust point", icon: "BadgeCheck" })}
      addLabel="Add trust point"
      saving={saving}
      onSave={save}
      renderItem={(tp, i, patch) => (
        <div className="space-y-2">
          <Input
            value={tp.label}
            onChange={(e) => patch({ label: e.target.value })}
            placeholder="Locally Owned"
            data-testid={`trust-point-label-${i}`}
          />
          <IconSelect
            value={tp.icon}
            options={TRUST_ICON_OPTIONS}
            onChange={(v) => patch({ icon: v })}
            testId={`trust-point-icon-${i}`}
          />
        </div>
      )}
    />
  );
}

export function WhyChooseUsEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    // Iter 71B — fall back to the SRC template's canonical WHY_US
    // defaults (6 items). Template shape is {title, body, icon};
    // editor shape is {title, description, icon}. The adapter reads
    // `w.description || w.body` so both saved-DB and template-default
    // items normalize into the same local shape.
    return resolveWithFallback(wc.why_choose_us, SVC_WHY_US_DEFAULTS, (w) => ({
      title: w.title || w.name || "",
      description: w.description || w.body || "",
      icon: w.icon || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((w) => (w.title || "").trim());
      await saveAll({ why_choose_us: clean });
      toast.success("Why-choose-us saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save why-choose-us");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="why-choose-us-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ title: "New reason", description: "", icon: "Award" })}
      addLabel="Add reason"
      saving={saving}
      onSave={save}
      renderItem={(w, i, patch) => (
        <div className="space-y-2">
          <Input
            value={w.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Reason title"
            data-testid={`why-choose-title-${i}`}
          />
          <Textarea
            rows={3}
            value={w.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder="Short description"
            data-testid={`why-choose-description-${i}`}
          />
          <IconSelect
            value={w.icon}
            options={WHY_ICON_OPTIONS}
            onChange={(v) => patch({ icon: v })}
            testId={`why-choose-icon-${i}`}
          />
        </div>
      )}
    />
  );
}

export function ProcessStepsEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    // Iter 71B — fall back to the SRC template's canonical
    // PROCESS_STEPS defaults (3 items). Template shape is
    // {number, title, body}; editor shape is {title, description}. The
    // adapter drops `number` (the backend regenerates "01"/"02"/"03"
    // when building service_config) and maps body → description.
    return resolveWithFallback(wc.how_it_works, SVC_PROCESS_STEPS_DEFAULTS, (s) => ({
      title: s.title || s.name || "",
      description: s.description || s.body || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((s) => (s.title || "").trim());
      await saveAll({ how_it_works: clean });
      toast.success("Process steps saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save process steps");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="process-steps-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ title: "New step", description: "" })}
      addLabel="Add step"
      saving={saving}
      onSave={save}
      renderItem={(s, i, patch) => (
        <div className="space-y-2">
          <Input
            value={s.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Step title"
            data-testid={`process-step-title-${i}`}
          />
          <Textarea
            rows={3}
            value={s.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder="Step description"
            data-testid={`process-step-description-${i}`}
          />
        </div>
      )}
    />
  );
}

export function AnnouncementMessagesEditor({ publicPayload, saveAll }) {
  const initial = useMemo(() => {
    const wc = publicPayload?.website_content || {};
    // Iter 71B — fall back to the SRC ecommerce template's canonical
    // DEFAULT_MESSAGES (4 items). Icons are already stored as string
    // keys in the canonical constant ("truck", "refresh", …) so no
    // conversion is needed here.
    return resolveWithFallback(wc.announcement_messages, ECOM_ANNOUNCEMENT_DEFAULTS, (m) => ({
      text: m.text || m.message || "",
      icon: m.icon || "",
    }));
  }, [publicPayload]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = items.filter((m) => (m.text || "").trim());
      await saveAll({ announcement_messages: clean });
      toast.success("Announcement messages saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save announcement messages");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListEditorShell
      testId="announcement-messages-editor"
      items={items}
      setItems={setItems}
      onAdd={() => ({ text: "FREE SHIPPING OVER $100", icon: "truck" })}
      addLabel="Add announcement"
      saving={saving}
      onSave={save}
      renderItem={(m, i, patch) => (
        <div className="space-y-2">
          <Input
            value={m.text}
            onChange={(e) => patch({ text: e.target.value })}
            placeholder="Announcement text"
            data-testid={`announcement-text-${i}`}
          />
          <IconSelect
            value={m.icon}
            options={ANNOUNCEMENT_ICON_OPTIONS}
            onChange={(v) => patch({ icon: v })}
            testId={`announcement-icon-${i}`}
          />
        </div>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Shared list editor shell                                                   */
/* -------------------------------------------------------------------------- */

function normalize(items, fn) {
  if (!Array.isArray(items)) return [];
  return items.map(fn);
}

function ListEditorShell({ testId, items, setItems, onAdd, addLabel, saving, onSave, renderItem }) {
  const patch = (i, p) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...p } : it));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((arr) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/40" data-testid={`${testId}-item-${i}`}>
          <div className="flex items-center gap-1 mb-2">
            <GripVertical className="w-3 h-3 text-gray-300" />
            <span className="text-[11px] font-black text-gray-500 uppercase tracking-wider">#{i + 1}</span>
            <div className="flex-1" />
            <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`${testId}-move-up-${i}`} title="Move up">
              <ArrowUp className="w-3 h-3" />
            </button>
            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30" data-testid={`${testId}-move-down-${i}`} title="Move down">
              <ArrowDown className="w-3 h-3" />
            </button>
            <button onClick={() => remove(i)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`${testId}-remove-${i}`} title="Remove">
              <X className="w-3 h-3" />
            </button>
          </div>
          {renderItem(it, i, (p) => patch(i, p))}
        </div>
      ))}
      <button
        onClick={() => setItems((arr) => [...arr, onAdd()])}
        className="w-full py-2 border-2 border-dashed border-emerald-300 text-emerald-700 rounded-md text-xs font-bold hover:border-emerald-500 flex items-center justify-center gap-1"
        data-testid={`${testId}-add`}
      >
        <Plus className="w-3 h-3" /> {addLabel}
      </button>
      <Button
        onClick={onSave}
        disabled={saving}
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
        data-testid={`${testId}-save`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Save
      </Button>
    </div>
  );
}
